import { Link } from "react-router-dom";
import {
  HeartHandshake, Sparkles, Leaf, Compass, Award, Infinity as InfinityIcon,
  FileText, BookOpen, Presentation, ArrowRight, Quote,
} from "lucide-react";
import { OrnamentalDivider } from "@/components/OrnamentalDivider";
import EnquiryForm from "@/components/EnquiryForm";
import DirectBookingCard from "@/components/DirectBookingCard";
import { IMAGES } from "@/data/content";

const PILLARS = [
  {
    letter: "B",
    title: "Blessings",
    icon: HeartHandshake,
    body: "Cultivating an attitude of gratitude — noticing the everyday goodness we walk past without seeing.",
  },
  {
    letter: "A",
    title: "Affirmations",
    icon: Sparkles,
    body: "Affirming oneself for success. Words shape the inner world before they shape the outer one.",
  },
  {
    letter: "S",
    title: "Stillness",
    icon: Leaf,
    body: "Being still and knowing it's all divine. Quiet is where clarity is made.",
  },
  {
    letter: "I",
    title: "Intentions",
    icon: Compass,
    body: "Setting clear intentions — pointing the day, the week, the year in a chosen direction.",
  },
  {
    letter: "C",
    title: "Confidence",
    icon: Award,
    body: "Promoting confidence and clarity that comes from the inside, not from applause.",
  },
  {
    letter: "U",
    title: "Unity of Life",
    icon: InfinityIcon,
    body: "The Oneness that ties it all together — a felt sense that we are not, and never have been, alone.",
  },
];

const INCLUDED = [
  { icon: FileText, title: "Worksheets", body: "Reflective exercises participants complete individually and in small groups." },
  { icon: BookOpen, title: "A Booklet", body: "A keep-it-forever companion to revisit long after the session ends." },
  { icon: Presentation, title: "Live Presentation", body: "90 minutes of thought-provoking, life-changing storytelling delivered in person." },
];

const AIMS = [
  "Develop an attitude of gratitude",
  "Affirm oneself for success",
  "Be still and know it's all divine",
  "Set clear, meaningful intentions",
  "Promote confidence and clarity",
  "Feel the Oneness of Life",
];

export default function Education() {
  return (
    <div data-testid="education-page">
      {/* HERO */}
      <section className="relative bg-[#1E4637] text-[#FDFBF7] overflow-hidden">
        <div className="absolute inset-0 paper-texture opacity-[0.06]" />
        <div className="relative max-w-6xl mx-auto px-6 lg:px-10 py-32 lg:py-40 text-center">
          <div className="stamp bg-[#FDFBF7]/10 border-[#E7C766] text-[#E7C766] mx-auto w-fit">
            Education Program
          </div>
          <h1 className="serif text-6xl md:text-8xl lg:text-9xl mt-8 tracking-tight" data-testid="basic-unity-title">
            <span className="italic text-[#E7C766]">Basic</span> Unity
          </h1>
          <div className="ornament-divider max-w-md mx-auto mt-8 mb-6 opacity-70">
            <span className="text-[#D4AF37]">✦</span>
            <span className="serif italic text-[#E7C766] text-sm tracking-widest">
              Blessings · Affirmations · Stillness · Intentions · Confidence · Unity
            </span>
            <span className="text-[#D4AF37]">✦</span>
          </div>
          <p className="text-lg md:text-xl text-[#F5EFEB]/90 max-w-3xl mx-auto leading-relaxed">
            A 90-minute wellbeing program delivered to schools and businesses —
            a personal transformation from the inside-out.
          </p>
          <div className="mt-10 flex flex-wrap gap-4 justify-center">
            <Link
              to="#enquire"
              onClick={(e) => {
                e.preventDefault();
                document.getElementById("enquire")?.scrollIntoView({ behavior: "smooth" });
              }}
              className="btn-primary bg-[#E7C766] text-[#1E4637] hover:bg-[#D4AF37]"
              data-testid="hero-enquire-btn"
            >
              Enquire for your school <ArrowRight size={18} />
            </Link>
            <Link to="/about" className="btn-outline-gold text-[#FDFBF7] border-[#E7C766]">
              About Martin
            </Link>
          </div>
        </div>
      </section>

      {/* INTRO */}
      <section className="max-w-5xl mx-auto px-6 lg:px-10 py-24">
        <div className="max-w-3xl">
          <div className="stamp">What is BASIC UNITY?</div>
          <h2 className="serif text-4xl md:text-5xl text-[#6E2638] mt-4 leading-tight">
            Six practices. Ninety minutes. One shift in perspective.
          </h2>
        </div>
        <OrnamentalDivider />
        <p className="text-[#1A1817]/85 leading-relaxed text-lg max-w-3xl">
          BASIC UNITY is a wellbeing program deliverable to schools and businesses, centered
          around <b>Blessings, Affirmations, Stillness, Intentions, Confidence</b> and the
          <b> Oneness / Unity of Life</b>. It combines worksheets, a keepsake booklet, and an
          unbelievably thought-provoking, life-changing presentation aimed at helping
          participants transform from the inside-out.
        </p>
      </section>

      {/* SIX PILLARS */}
      <section className="bg-[#F5EFEB] border-y border-[#E5D9C5] py-24" data-testid="pillars-section">
        <div className="max-w-6xl mx-auto px-6 lg:px-10">
          <div className="text-center max-w-2xl mx-auto">
            <div className="stamp">The Six Pillars</div>
            <h2 className="serif text-4xl md:text-5xl text-[#6E2638] mt-4">
              Each letter, one practice.
            </h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mt-16">
            {PILLARS.map(({ letter, title, icon: Icon, body }) => (
              <div
                key={letter}
                className="card-warm rounded-2xl p-8 flex flex-col"
                data-testid={`pillar-${title.toLowerCase().replace(/\s+/g, "-")}`}
              >
                <div className="flex items-center gap-4">
                  <div className="serif text-6xl text-[#D4AF37]/80 leading-none">{letter}</div>
                  <div className="w-10 h-10 rounded-full bg-[#1E4637] text-[#E7C766] flex items-center justify-center">
                    <Icon size={18} />
                  </div>
                </div>
                <div className="serif text-2xl text-[#6E2638] mt-4">{title}</div>
                <p className="text-sm text-[#1A1817]/80 mt-3 leading-relaxed">{body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHAT'S INCLUDED */}
      <section className="max-w-6xl mx-auto px-6 lg:px-10 py-24">
        <div className="max-w-2xl">
          <div className="stamp">What's included</div>
          <h2 className="serif text-4xl md:text-5xl text-[#6E2638] mt-4">
            Everything your group needs, on the day.
          </h2>
        </div>
        <OrnamentalDivider />
        <div className="grid md:grid-cols-3 gap-6">
          {INCLUDED.map(({ icon: Icon, title, body }) => (
            <div key={title} className="card-warm rounded-2xl p-8" data-testid={`included-${title.toLowerCase().replace(/\s+/g, "-")}`}>
              <div className="w-14 h-14 rounded-full bg-[#6E2638] text-[#FDFBF7] flex items-center justify-center">
                <Icon size={22} />
              </div>
              <div className="serif text-2xl text-[#6E2638] mt-6">{title}</div>
              <p className="text-sm text-[#1A1817]/80 mt-3 leading-relaxed">{body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* AIMS */}
      <section className="bg-[#1E4637] text-[#F5EFEB] py-24" data-testid="aims-section">
        <div className="max-w-5xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-5">
            <div className="stamp bg-[#FDFBF7]/10 border-[#E7C766] text-[#E7C766] w-fit">
              What participants take away
            </div>
            <h2 className="serif text-4xl md:text-5xl text-[#E7C766] mt-4 leading-tight">
              A shift you can feel — and keep.
            </h2>
          </div>
          <ul className="lg:col-span-7 space-y-4">
            {AIMS.map((aim, i) => (
              <li key={aim} className="flex items-start gap-4 border-b border-[#FDFBF7]/10 pb-4">
                <span className="serif text-3xl text-[#E7C766]/70 leading-none w-10">0{i + 1}</span>
                <span className="serif text-xl text-[#FDFBF7] leading-snug">{aim}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* CLOSING QUOTE */}
      <section className="relative py-24">
        <div className="absolute inset-0 paper-texture opacity-[0.08]" />
        <div className="relative max-w-4xl mx-auto px-6 lg:px-10 text-center">
          <Quote size={40} className="mx-auto text-[#D4AF37]" />
          <blockquote className="serif italic text-2xl md:text-4xl text-[#1A1817] mt-6 leading-snug">
            BASIC UNITY is your school's one-stop shop for a personal transformation
            <span className="text-[#6E2638]"> from the inside-out.</span>
          </blockquote>
        </div>
      </section>

      {/* ENQUIRY */}
      <section id="enquire" className="max-w-6xl mx-auto px-6 lg:px-10 py-16 grid lg:grid-cols-5 gap-10 scroll-mt-24">
        <div className="lg:col-span-2 space-y-6">
          <div className="stamp">Bring BASIC UNITY to your group</div>
          <h2 className="serif text-3xl text-[#6E2638] leading-tight">
            Schools, staff wellbeing days, retreats, leadership offsites.
          </h2>
          <p className="text-[#1A1817]/85 leading-relaxed">
            Send an enquiry and Martin will follow up with a proposed date, format, and a
            tailored quote for your group size.
          </p>
          <DirectBookingCard />
        </div>
        <div className="lg:col-span-3">
          <EnquiryForm
            tourType="education"
            heading="Enquire about a BASIC UNITY session"
            orgLabel="School / Organisation"
          />
        </div>
      </section>
    </div>
  );
}
